import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import {
  type ClientAccountsAdminQuery,
  clientAccountsAdminV2Service
} from '../services/client-accounts-admin-v2.service'

/** El token v2 de la sesión staff. Vacío mientras la sesión no está lista. */
function useV2AccessToken(): string {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

type UseClientAccountsQueryArgs = {
  filters?: Record<string, unknown>
  sort?: string
  search?: string
  pageSize: number
}

/**
 * El listado con paginación por cursor y navegación prev/next por PILA (ADR 0019).
 *
 * Espeja los hooks de la ola: la pila guarda el `after` de cada página, `goNext` apila el
 * `nextCursor` y `goPrev` desapila. Cambiar filtros, orden, búsqueda o tamaño reinicia la
 * pila — un cursor está atado a la consulta que lo emitió y arrastrarlo entre consultas
 * distintas devuelve una página que no corresponde.
 */
export function useClientAccountsAdminV2Query({
  filters,
  sort,
  search,
  pageSize
}: UseClientAccountsQueryArgs) {
  const accessToken = useV2AccessToken()

  const resetKey = JSON.stringify({ filters, sort, search, pageSize })
  const [cursorState, setCursorState] = useState<{ key: string; stack: (string | undefined)[] }>({
    key: resetKey,
    stack: [undefined]
  })
  if (cursorState.key !== resetKey) {
    setCursorState({ key: resetKey, stack: [undefined] })
  }
  const after = cursorState.stack[cursorState.stack.length - 1]

  const listQuery: ClientAccountsAdminQuery = { filters, sort, search, pageSize, after }
  const query = useQuery({
    queryKey: queryKeys.clientAccounts.adminListV2({ filters, sort, search, pageSize, after }),
    queryFn: async () => await clientAccountsAdminV2Service.list(accessToken, listQuery),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // Un 403/404 es determinista: reintentarlo sólo retrasa el aviso (ADR 0031).
    retry: false
  })

  const nextCursor = query.data?.pagination.nextCursor
  const goNext = useCallback(() => {
    if (!nextCursor) return
    setCursorState((state) => ({ ...state, stack: [...state.stack, nextCursor] }))
  }, [nextCursor])

  const goPrev = useCallback(() => {
    setCursorState((state) =>
      state.stack.length > 1 ? { ...state, stack: state.stack.slice(0, -1) } : state
    )
  }, [])

  return {
    query,
    rows: query.data?.data ?? [],
    /**
     * `true` cuando la búsqueda por nombre tocó su tope en el servidor.
     *
     * Se propaga hasta la pantalla en vez de tragarse: un listado truncado en silencio se
     * lee como «tu cliente no existe», que es la respuesta equivocada a «lo busqué y no
     * aparece».
     */
    searchTruncated: query.data?.searchTruncated ?? false,
    page: cursorState.stack.length,
    canPrev: cursorState.stack.length > 1,
    canNext: query.data?.pagination.hasMore ?? false,
    goNext,
    goPrev
  }
}

/** Los contadores de las tarjetas: mismos filtros, sin paginar. */
export function useClientAccountsAdminV2Summary(args: {
  filters?: Record<string, unknown>
  search?: string
}) {
  const accessToken = useV2AccessToken()
  return useQuery({
    queryKey: queryKeys.clientAccounts.summaryV2(args),
    queryFn: async () => await clientAccountsAdminV2Service.summary(accessToken, args),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false
  })
}

/** El detalle de UNA cuenta: accesos, dispositivos y bitácora. */
export function useClientAccountDetailV2Query(id: string | null) {
  const accessToken = useV2AccessToken()
  return useQuery({
    queryKey: queryKeys.clientAccounts.detailV2(id),
    queryFn: async () => await clientAccountsAdminV2Service.detail(accessToken, id as string),
    enabled: Boolean(accessToken) && Boolean(id),
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * Las cuentas de UN cliente, para la pestaña «App» de su ficha.
 *
 * Reusa el listado con `filters[clientId]` en vez de un endpoint propio: es la misma
 * pregunta acotada, y un endpoint aparte sería una segunda definición de qué es una
 * cuenta que habría que mantener sincronizada.
 */
export function useClientAccountsByClientQuery(clientId: string | null) {
  const accessToken = useV2AccessToken()
  return useQuery({
    queryKey: queryKeys.clientAccounts.byClientV2(clientId),
    queryFn: async () =>
      await clientAccountsAdminV2Service.list(accessToken, {
        filters: { clientId: { $eq: clientId } },
        pageSize: 20
      }),
    enabled: Boolean(accessToken) && Boolean(clientId),
    refetchOnWindowFocus: false,
    retry: false
  })
}
